import { useState, useEffect } from "react";
import { getAllProducts } from "../../services/productService";
import { getAllInventoryLogs } from "../../services/inventoryLogService";
import { exportToExcel, exportToPDF } from "../../utils/exportUtils";
import { toast } from "react-hot-toast";
import {
    Package,
    FileSpreadsheet,
    FileText,
    Search,
    Calendar,
    Filter,
    TrendingUp,
    TrendingDown,
} from "lucide-react";

export default function ProductReport() {
    const [products, setProducts] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filter states
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedProduct, setSelectedProduct] = useState("");
    const [filterType, setFilterType] = useState("all"); // all, day, month, custom
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [products, transactions, searchTerm, selectedProduct, filterType, selectedDate, selectedMonth, customFrom, customTo]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [productsData, logsData] = await Promise.all([
                getAllProducts(),
                getAllInventoryLogs(),
            ]);
            setProducts(productsData);
            setTransactions(logsData);
        } catch (error) {
            toast.error("Failed to fetch data");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateProductStats = (productId) => {
        const productLogs = transactions.filter((l) => l.productId === productId);

        let inboundQty = 0;
        let outboundQty = 0;
        let inboundAmount = 0;
        let outboundAmount = 0;
        let inboundPoints = 0;
        let outboundPoints = 0;

        const productInfo = products.find(p => p.id === productId);
        const unitPrice = Number(productInfo?.mrp || productInfo?.purchasePrice || 0);
        const unitVP = Number(productInfo?.volPoint || 0);

        productLogs.forEach((log) => {
            const qtyChange = Number(log.quantityChange || 0);
            const absQtyChange = Math.abs(qtyChange);
            const amount = unitPrice * absQtyChange;
            const points = unitVP * absQtyChange;

            if (qtyChange > 0) {
                inboundQty += absQtyChange;
                inboundAmount += amount;
                inboundPoints += points;
            } else if (qtyChange < 0) {
                outboundQty += absQtyChange;
                outboundAmount += amount;
                outboundPoints += points;
            }
        });

        return {
            inboundQty,
            outboundQty,
            inboundAmount,
            outboundAmount,
            inboundPoints,
            outboundPoints,
            balance: inboundQty - outboundQty,
            balanceAmount: inboundAmount - outboundAmount,
            balancePoints: inboundPoints - outboundPoints,
        };
    };

    const applyFilters = () => {
        let filtered = products.map((product) => {
            const stats = calculateProductStats(product.id);
            return {
                ...product,
                ...stats,
            };
        });

        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (p) =>
                    p.name?.toLowerCase().includes(searchLower) ||
                    p.productCode?.toLowerCase().includes(searchLower)
            );
        }

        // Product filter
        if (selectedProduct) {
            filtered = filtered.filter((p) => p.id === selectedProduct);
        }

        // Date/Month filter
        if (filterType === "day" && selectedDate) {
            const dateLogs = transactions.filter((l) => l.date === selectedDate);
            filtered = filtered.map((product) => {
                const stats = calculateProductStatsFromLogs(
                    product.id,
                    dateLogs
                );
                return { ...product, ...stats };
            });
        } else if (filterType === "month" && selectedMonth) {
            const monthLogs = transactions.filter((l) => l.date && l.date.startsWith(selectedMonth));
            filtered = filtered.map((product) => {
                const stats = calculateProductStatsFromLogs(
                    product.id,
                    monthLogs
                );
                return { ...product, ...stats };
            });
        } else if (filterType === "custom" && customFrom && customTo) {
            const customLogs = transactions.filter((l) => l.date && l.date >= customFrom && l.date <= customTo);
            filtered = filtered.map((product) => {
                const stats = calculateProductStatsFromLogs(
                    product.id,
                    customLogs
                );
                return { ...product, ...stats };
            });
        }

        setFilteredData(filtered);
    };

    const calculateProductStatsFromLogs = (productId, logsList) => {
        const productLogs = logsList.filter((l) => l.productId === productId);

        let inboundQty = 0;
        let outboundQty = 0;
        let inboundAmount = 0;
        let outboundAmount = 0;
        let inboundPoints = 0;
        let outboundPoints = 0;

        const productInfo = products.find(p => p.id === productId);
        const unitPrice = Number(productInfo?.mrp || productInfo?.purchasePrice || 0);
        const unitVP = Number(productInfo?.volPoint || 0);

        productLogs.forEach((log) => {
            const qtyChange = Number(log.quantityChange || 0);
            const absQtyChange = Math.abs(qtyChange);
            const amount = unitPrice * absQtyChange;
            const points = unitVP * absQtyChange;

            if (qtyChange > 0) {
                inboundQty += absQtyChange;
                inboundAmount += amount;
                inboundPoints += points;
            } else if (qtyChange < 0) {
                outboundQty += absQtyChange;
                outboundAmount += amount;
                outboundPoints += points;
            }
        });

        return {
            inboundQty,
            outboundQty,
            inboundAmount,
            outboundAmount,
            inboundPoints,
            outboundPoints,
            balance: inboundQty - outboundQty,
            balanceAmount: inboundAmount - outboundAmount,
            balancePoints: inboundPoints - outboundPoints,
        };
    };

    const handleExportExcel = () => {
        try {
            const exportData = filteredData.map((product) => ({
                "Product Name": product.name,
                "Product Code": product.productCode,
                "Price": product.mrp,
                "UOM": product.uom || "N/A",
                "Value Points": product.volPoint,
                "Inbound Qty": product.inboundQty,
                "Inbound Amount": product.inboundAmount?.toFixed(2),
                "Purchase VP": product.inboundPoints?.toFixed(2),
                "Outbound Qty": product.outboundQty,
                "Outbound Amount": product.outboundAmount?.toFixed(2),
                "Club Used VP": product.outboundPoints?.toFixed(2),
                "Balance Qty": product.balance,
                "Balance Amount": product.balanceAmount?.toFixed(2),
                "Balance VP": product.balancePoints?.toFixed(2),
            }));

            // Append a Total row
            exportData.push({
                "Product Name": "TOTAL",
                "Product Code": "",
                "Price": "",
                "UOM": "",
                "Value Points": "",
                "Inbound Qty": filteredData.reduce((sum, p) => sum + (Number(p.inboundQty) || 0), 0),
                "Inbound Amount": filteredData.reduce((sum, p) => sum + (Number(p.inboundAmount) || 0), 0).toFixed(2),
                "Purchase VP": filteredData.reduce((sum, p) => sum + (Number(p.inboundPoints) || 0), 0).toFixed(2),
                "Outbound Qty": filteredData.reduce((sum, p) => sum + (Number(p.outboundQty) || 0), 0),
                "Outbound Amount": filteredData.reduce((sum, p) => sum + (Number(p.outboundAmount) || 0), 0).toFixed(2),
                "Club Used VP": filteredData.reduce((sum, p) => sum + (Number(p.outboundPoints) || 0), 0).toFixed(2),
                "Balance Qty": filteredData.reduce((sum, p) => sum + (Number(p.balance) || 0), 0),
                "Balance Amount": filteredData.reduce((sum, p) => sum + (Number(p.balanceAmount) || 0), 0).toFixed(2),
                "Balance VP": filteredData.reduce((sum, p) => sum + (Number(p.balancePoints) || 0), 0).toFixed(2),
            });

            exportToExcel(exportData, `product_report_${Date.now()}`);
            toast.success("Excel file downloaded successfully!");
        } catch (error) {
            toast.error("Failed to export Excel");
            console.error("Error:", error);
        }
    };

    const handleExportPDF = () => {
        try {
            const head = [[
                "Product Name", "Code", "Price", "UOM", "VP",
                "In Qty", "In Amt", "Pur VP", "Out Qty", "Out Amt",
                "Club VP", "Bal Qty", "Bal Amt", "Bal VP"
            ]];
            const body = filteredData.map((p) => [
                p.name, p.productCode, p.mrp, p.uom || "N/A", p.volPoint,
                p.inboundQty, p.inboundAmount?.toFixed(2), p.inboundPoints?.toFixed(2),
                p.outboundQty, p.outboundAmount?.toFixed(2), p.outboundPoints?.toFixed(2),
                p.balance, p.balanceAmount?.toFixed(2), p.balancePoints?.toFixed(2)
            ]);

            body.push([
                "TOTAL", "", "", "", "",
                filteredData.reduce((sum, p) => sum + (Number(p.inboundQty) || 0), 0),
                filteredData.reduce((sum, p) => sum + (Number(p.inboundAmount) || 0), 0).toFixed(2),
                filteredData.reduce((sum, p) => sum + (Number(p.inboundPoints) || 0), 0).toFixed(2),
                filteredData.reduce((sum, p) => sum + (Number(p.outboundQty) || 0), 0),
                filteredData.reduce((sum, p) => sum + (Number(p.outboundAmount) || 0), 0).toFixed(2),
                filteredData.reduce((sum, p) => sum + (Number(p.outboundPoints) || 0), 0).toFixed(2),
                filteredData.reduce((sum, p) => sum + (Number(p.balance) || 0), 0),
                filteredData.reduce((sum, p) => sum + (Number(p.balanceAmount) || 0), 0).toFixed(2),
                filteredData.reduce((sum, p) => sum + (Number(p.balancePoints) || 0), 0).toFixed(2),
            ]);

            exportToPDF({
                title: "Product Report",
                head,
                body,
                fileName: `product_report_${Date.now()}`
            });
            toast.success("PDF file downloaded successfully!");
        } catch (error) {
            toast.error("Failed to export PDF");
            console.error("Error:", error);
        }
    };

    const resetFilters = () => {
        setSearchTerm("");
        setSelectedProduct("");
        setFilterType("all");
        setSelectedDate("");
        setSelectedMonth("");
        setCustomFrom("");
        setCustomTo("");
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Package className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Product Report</h1>
                                <p className="text-sm text-gray-500">
                                    Comprehensive product inventory and transaction history
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleExportExcel}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                            >
                                <FileSpreadsheet className="w-4 h-4" />
                                Excel
                            </button>
                            <button
                                onClick={handleExportPDF}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                            >
                                <FileText className="w-4 h-4" />
                                PDF
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="w-5 h-5 text-gray-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search products..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Product Filter */}
                        <select
                            value={selectedProduct}
                            onChange={(e) => setSelectedProduct(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Products</option>
                            {products.map((product) => (
                                <option key={product.id} value={product.id}>
                                    {product.name} ({product.productCode})
                                </option>
                            ))}
                        </select>

                        {/* Filter Type */}
                        <select
                            value={filterType}
                            onChange={(e) => {
                                setFilterType(e.target.value);
                                setSelectedDate("");
                                setSelectedMonth("");
                                setCustomFrom("");
                                setCustomTo("");
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Time</option>
                            <option value="day">By Day</option>
                            <option value="month">By Month</option>
                            <option value="custom">Custom Range</option>
                        </select>

                        {/* Date/Month Picker */}
                        {filterType === "day" && (
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                        {filterType === "month" && (
                            <input
                                type="month"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        )}
                        {filterType === "custom" && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="date"
                                    value={customFrom}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                                />
                                <span className="text-gray-500 text-sm">to</span>
                                <input
                                    type="date"
                                    value={customTo}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                                />
                            </div>
                        )}
                    </div>

                    <div className="mt-4">
                        <button
                            onClick={resetFilters}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                            <TrendingUp className="w-8 h-8 opacity-80" />
                            <span className="text-sm opacity-80">Total Inbound</span>
                        </div>
                        <div className="text-3xl font-bold">
                            {filteredData.reduce((sum, p) => sum + p.inboundQty, 0)}
                        </div>
                        <div className="text-sm opacity-90 mt-1">
                            ₹{filteredData.reduce((sum, p) => sum + p.inboundAmount, 0).toLocaleString()}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                            <TrendingDown className="w-8 h-8 opacity-80" />
                            <span className="text-sm opacity-80">Total Outbound</span>
                        </div>
                        <div className="text-3xl font-bold">
                            {filteredData.reduce((sum, p) => sum + p.outboundQty, 0)}
                        </div>
                        <div className="text-sm opacity-90 mt-1">
                            ₹{filteredData.reduce((sum, p) => sum + p.outboundAmount, 0).toLocaleString()}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
                        <div className="flex items-center justify-between mb-2">
                            <Package className="w-8 h-8 opacity-80" />
                            <span className="text-sm opacity-80">Current Balance</span>
                        </div>
                        <div className="text-3xl font-bold">
                            {filteredData.reduce((sum, p) => sum + p.balance, 0)}
                        </div>
                        <div className="text-sm opacity-90 mt-1">
                            {filteredData.length} Products
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50">
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Product Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Code
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Price
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        VP
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                                        Inbound Qty
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                                        Inbound Amt
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                                        Purchase VP
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-50">
                                        Outbound Qty
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-50">
                                        Outbound Amt
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-50">
                                        Club Used VP
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                                        Balance Qty
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                                        Balance VP
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="12" className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                <p className="text-gray-500">Loading products...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="12" className="px-6 py-12 text-center">
                                            <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                                            <p className="text-gray-500">No products found</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((product) => (
                                        <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    <span>{product.name}</span>
                                                    {product.isSpecial && (
                                                        <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-md border border-amber-200">Special</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {product.productCode}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                                ₹{product.mrp?.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-purple-700 font-medium">
                                                {product.volPoint || 0}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium bg-green-50/30">
                                                {product.inboundQty}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 bg-green-50/30">
                                                ₹{product.inboundAmount?.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-green-700 font-semibold bg-green-50/30">
                                                {(product.inboundPoints || 0).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-orange-600 font-medium bg-orange-50/30">
                                                {product.outboundQty}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-orange-600 bg-orange-50/30">
                                                ₹{product.outboundAmount?.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-orange-700 font-semibold bg-orange-50/30">
                                                {(product.outboundPoints || 0).toFixed(2)}
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-bold bg-blue-50/30">
                                                <span className={product.balance >= 0 ? "text-blue-600" : "text-red-600"}>
                                                    {product.balance}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap text-sm font-bold bg-blue-50/30">
                                                <span className={product.balancePoints >= 0 ? "text-blue-600" : "text-red-600"}>
                                                    {(product.balancePoints || 0).toFixed(2)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
